import { t } from "@lingui/core/macro"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft, Plus, Users } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { z } from "zod"
import type { Environment, FlagUserOverride } from "@/features/feature-flags/types/flagTypes"
import { ConfirmDialog } from "@/components/ConfirmDialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { flagDetailQuery } from "@/features/feature-flags/components/FlagDetail"
import { UserOverridesTable } from "@/features/feature-flags/components/UserOverridesTable"
import { deleteUserOverrideFn } from "@/features/feature-flags/server/flagManagement"
import { usePermissions } from "@/hooks/usePermissions"
import { flagQueryKeys } from "@/lib/queryKeys"

const Params = z.object({
  flagId: z.string(),
})

const Search = z.object({
  environment: z.enum(["dev", "staging", "production"]).optional(),
})

export const Route = createFileRoute("/(authenticated)/settings/feature-flags/$flagId/overrides/")({
  params: Params,
  validateSearch: Search,
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(flagDetailQuery(params.flagId))
  },
  component: OverridesListPage,
})

function OverridesListPage() {
  const { flagId } = Route.useParams()
  const { environment: initialEnv } = Route.useSearch()
  const queryClient = useQueryClient()
  const { hasPermission } = usePermissions()
  const { data: flag } = useSuspenseQuery(flagDetailQuery(flagId))

  const canUpdate = hasPermission("featureFlag", "update").allowed
  const isArchived = !!flag.archivedAt

  const [selectedEnv] = useState<Environment | "all">(initialEnv ?? "production")
  const [deleteOverrideOpen, setDeleteOverrideOpen] = useState(false)
  const [overrideToDelete, setOverrideToDelete] = useState<string | null>(null)

  // Filter overrides by environment
  const filteredOverrides: FlagUserOverride[] =
    selectedEnv === "all"
      ? flag.flagUserOverrides
      : flag.flagUserOverrides.filter((o) => o.environment === selectedEnv)

  const deleteOverrideMutation = useMutation({
    mutationFn: deleteUserOverrideFn,
    onSuccess: () => {
      toast.success(t`Override deleted`)
      setDeleteOverrideOpen(false)
      setOverrideToDelete(null)
      void queryClient.invalidateQueries({ queryKey: flagQueryKeys.detail(flagId) })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t`Failed to delete override`)
    },
  })

  const handleDeleteOverride = (overrideId: string) => {
    setOverrideToDelete(overrideId)
    setDeleteOverrideOpen(true)
  }

  const confirmDeleteOverride = () => {
    if (!overrideToDelete) return
    deleteOverrideMutation.mutate({ data: { overrideId: overrideToDelete } })
  }

  return (
    <>
      <div className="flex-1 space-y-6 overflow-y-auto pb-8">
        <div className="flex items-start justify-between gap-6">
          <div>
            <Link
              to="/settings/feature-flags/$flagId"
              params={{ flagId }}
              className="mb-4 inline-flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t`Back to ${flag.name}`}
            </Link>
            <div className="mb-2 text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-300">
              {t`User Overrides`}
            </div>
            <h1 className="mb-2 font-serif text-4xl font-bold text-neutral-900 dark:text-white">
              {t`Override Management`}
            </h1>
            <p className="text-neutral-500 dark:text-neutral-300">
              {t`Manage user-specific overrides for ${flag.key}`}
            </p>
          </div>
          {canUpdate && !isArchived && (
            <Link
              to="/settings/feature-flags/$flagId/overrides/new"
              params={{ flagId }}
              search={selectedEnv !== "all" ? { environment: selectedEnv } : undefined}
            >
              <Button>
                <Plus className="mr-2 size-4" />
                {t`Add Override`}
              </Button>
            </Link>
          )}
        </div>

        {/* Environment selection hidden - always use production */}

        {isArchived && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
            <p className="text-amber-800 dark:text-amber-200">
              {t`This flag is archived. Overrides cannot be added or modified.`}
            </p>
          </div>
        )}

        {/* Overrides table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-5" />
              {selectedEnv === "all" ? t`All User Overrides` : t`${selectedEnv} Overrides`}
            </CardTitle>
            <CardDescription>
              {selectedEnv === "all"
                ? t`Showing ${filteredOverrides.length} override(s) across all environments`
                : t`Showing ${filteredOverrides.length} override(s) in ${selectedEnv}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredOverrides.length === 0 ? (
              <div className="py-8 text-center text-neutral-500">
                <Users className="mx-auto mb-2 h-8 w-8 opacity-50" />
                <p>
                  {selectedEnv === "all"
                    ? t`No user overrides configured for this flag`
                    : t`No user overrides for ${selectedEnv}`}
                </p>
                {canUpdate && !isArchived && (
                  <Link
                    to="/settings/feature-flags/$flagId/overrides/new"
                    params={{ flagId }}
                    search={selectedEnv !== "all" ? { environment: selectedEnv } : undefined}
                    className="mt-4 inline-block"
                  >
                    <Button variant="outline" size="sm">
                      <Plus className="mr-2 size-4" />
                      {t`Add First Override`}
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <UserOverridesTable
                overrides={filteredOverrides}
                canDelete={canUpdate && !isArchived}
                onDelete={handleDeleteOverride}
                isDeleting={deleteOverrideMutation.isPending}
                showEnvironment={selectedEnv === "all"}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        title={t`Delete Override`}
        description={t`Are you sure you want to delete this user override? The user will receive the standard flag evaluation.`}
        confirmLabel={t`Delete`}
        cancelLabel={t`Cancel`}
        open={deleteOverrideOpen}
        onOpenChange={(open) => {
          if (!deleteOverrideMutation.isPending) setDeleteOverrideOpen(open)
        }}
        onConfirm={confirmDeleteOverride}
        onCancel={() => {
          setDeleteOverrideOpen(false)
          setOverrideToDelete(null)
        }}
        loading={deleteOverrideMutation.isPending}
      />
    </>
  )
}
